"use client";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Award, Code, Globe, Rocket, Users, Zap } from "lucide-react";
import Link from "next/link";
import { SectionHeading } from "./common/section-heading";
import PhotoWithPulsingBorderShader from "./hero/pulsing-border-shader";
import { Separator } from "./ui/separator";

export function About() {
  // const sectionRef = useRef<HTMLElement>(null);

  // useEffect(() => {
  //     const observer = new IntersectionObserver(
  //         (entries) => {
  //             entries.forEach((entry) => {
  //                 if (entry.isIntersecting) {
  //                     entry.target.classList.add('animate-fade-in-up');
  //                 }
  //             });
  //         },
  //         { threshold: 0.1 }
  //     );

  //     const elements =
  //         sectionRef.current?.querySelectorAll('.animate-on-scroll');
  //     elements?.forEach((el) => observer.observe(el));

  //     return () => observer.disconnect();
  // }, []);

  const features = [
    {
      icon: Code,
      title: "Clean Code",
      description:
        "Writing maintainable, scalable, and efficient code following best practices and design patterns.",
    },
    {
      icon: Rocket,
      title: "Performance",
      description:
        "Optimizing applications for speed, SEO, and exceptional user experience with modern techniques.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description:
        "Working effectively in teams using modern development workflows and agile methodologies.",
    },
    {
      icon: Zap,
      title: "Innovation",
      description:
        "Staying current with latest technologies and implementing creative solutions for complex problems.",
    },
    {
      icon: Award,
      title: "Excellence",
      description:
        "Delivering high-quality solutions that exceed expectations and drive business value.",
    },
    {
      icon: Globe,
      title: "Global Reach",
      description:
        "Building applications that scale globally and serve diverse user bases effectively.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="about" className="relative mt-20 px-4 sm:px-6 lg:px-8">
      {/* className='py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-purple-50 dark:from-black dark:via-gray-900 dark:to-purple-900/20'> */}
      {/* <Separator className="bg-purple-800 dark:bg-white mb-12" /> */}
      <motion.div
        className="mx-auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        // viewport={{ once: true, amount: 0.3 }}
        viewport={{ once: true, amount: "some" }}
      >
        <motion.div className="text-center mb-10" variants={itemVariants}>
          <h2 className="text-5xl sm:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 dark:from-purple-400 dark:via-pink-400 dark:to-purple-400 bg-clip-text text-transparent">
              About Me
            </span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-8" />

          {/* <p className="text-lg md:text-xl text-muted-foreground max-2-2xl mx-auto animate-fade-in-delay-3"> */}
          {/* <p className="text-xl text-gray-600 dark:text-gray-400 mx-auto">
            Passionate about creating innovative digital solutions that make a
            difference. I create stellar web experiences with modern
            technologies. Specializing in front-end development, I build
            interfaces that are both beautiful and functional.
          </p> */}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
          <motion.div variants={itemVariants}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-xl"></div>
              <div
                className={cn(
                  "relative bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/20 shadow-2xl",
                  "space-y-3 text-gray-700 dark:text-gray-100 text-sm lg:text-base leading-relaxed break-words text-ellipsis"
                )}
              >
                <div className="flex flex-1 items-center justify-between">
                  {/* <div className="order-1 lg:order-2"> */}
                  <div className="relative">
                    <SectionHeading
                      title="My Journey"
                      // description="Transforming ideas into elegant digital solutions. Passionate about creating intuitive and performant web experiences that make a difference."
                      description="A quick look at my career as fullstack developer transforming ideas in to scalable cloud-native applications"
                    />
                  </div>
                  {/* <div className="order-2 lg:order-1"> */}
                  {/* <Photo
                      showPreview={true}
                      className="w-[80%] h-[80%] rounded-full overflow-hidden"
                    /> */}
                  {/* <PhotoOld showPreview={true} withBorderAnimation={true} /> */}

                  {/* Right side - Animation (NEW) */}
                  <div className="flex justify-center lg:justify-end">
                    <div className="relative">
                      {/* Glow effect behind the shader */}
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 blur-3xl scale-110" />

                      {/* Main shader component */}
                      {/* <PulsingBorderShader className="min-h-[96px] min-w-[96px] relative" /> */}
                      <PhotoWithPulsingBorderShader />

                      {/* Floating elements */}
                      <div
                        className="absolute top-0 right-4 w-3 h-3 bg-purple-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0s" }}
                      />
                      <div
                        className="absolute top-1/3 right-0 w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                        style={{ animationDelay: "1s" }}
                      />
                      <div
                        className="absolute top-1 left-4 w-4 h-4 bg-pink-400 rounded-full animate-bounce"
                        // className="absolute bottom-1/4 -right-8 w-4 h-4 bg-pink-400 rounded-full animate-bounce"
                        style={{ animationDelay: "2s" }}
                      />
                    </div>
                  </div>
                </div>

                {/* <p className="text-foreground-muted text-lg mb-6 max-w-2xl">
                  Transforming ideas into elegant digital solutions. Passionate
                  about creating intuitive and performant web experiences that
                  make a difference.
                </p> */}

                <p>
                  Engineer & Fullstack web developer specialist in building
                  high-impact web and cloud applications. With nearly 4 years of
                  expertise, I excel at architecting robust end-to-end solutions
                  from scalable backends to intuitive frontends and streamlining
                  deployment pipelines using MERN stack, Next.js, Rest
                  Api/GraphQL, Webscoket, Redis, ELK Stack, AWS, Docker, CI/CD,
                  AI/ML and more advanced apis, tools & techs.
                </p>
                {/* <p>
                  Passionate about creating innovative digital solutions that
                  make a difference. I create stellar web experiences with
                  modern technologies. Specializing in front-end development, I
                  build interfaces that are both beautiful and functional.
                </p> */}
                <p>
                  My strength lies in merging cutting-edge technologies
                  (including AI/ML services) into production-ready systems while
                  prioritizing measurable performance, security, and
                  user-centricity. Dedicated to implementing robust testing
                  strategies and CI/CD pipelines that ensure code quality and
                  streamline deployments. I drive scalable innovation by
                  leveraging modern DevOps practices and cloud-native
                  technologies to deliver reliable, high-performance
                  applications.
                </p>
                {/* <p>
                  Dedicated to creating code quality, testing and automating the
                  deployment pipelines using CI/CD best practices I drive
                  scalable innovation aligned with strategic goals.
                </p> */}
                <p>
                  You can check more work/projects of mine{" "}
                  <Link
                    className="underline text-blue-900 dark:text-blue-300 font-semibold text-lg"
                    href="https://www.notion.so/hamza-missaoui/Hamza-Missaoui-Projects-25bba7e86dab8067b6d9d183e6cfeb67"
                    target="_blank"
                  >
                    Here
                  </Link>
                </p>
                {/* <p>
                  My passion lies in transforming complex problems into elegant
                  solutions, leveraging the latest technologies to build
                  scalable and maintainable applications.
                </p> */}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 gap-8"
            variants={containerVariants}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-white/60 dark:bg-gray-800/60 border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 group h-full backdrop-blur-sm shadow-lg hover:shadow-2xl">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 inline-flex p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-all duration-300">
                      <feature.icon className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h4 className="text-2xl font-bold dark:text-white mb-4">
                      {feature.title}
                    </h4>
                    <p className="dark:text-gray-400 text-gray-700 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <Separator className="bg-purple-800 dark:bg-white mx-auto max-w-3xl mb-3" />

        {/* Stats Section */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12"
          variants={containerVariants}
        >
          {[
            { number: "4-", label: "Years Experience" },
            { number: "35+", label: "Projects Completed" },
            { number: "90%+", label: "Client Satisfaction" },
            { number: "99%", label: "Clean/Scalable Code" },
            // { number: "Garanteed", label: "Clean Code" },
            // { number: "24/7", label: "Support Available" },
            { number: "12/7", label: "Support Available" },
          ].map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="text-center"
            >
              <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                {stat.number}
              </div>
              <div className="text-gray-600 dark:text-gray-400 font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
